import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Users, Dna, PawPrint, ArrowUpRight } from "lucide-react";
import { BreedAnalytics } from "@/components/pawkt/BreedAnalytics";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : "—");

function Card({ icon: Icon, label, value, sub, tone, testid, delay, onClick, aria }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-label={aria} style={{ animationDelay: `${delay}ms` }}
      className="group relative overflow-hidden rounded-2xl border border-pawkt-border bg-white p-5 text-left shadow-card transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-pawkt-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pawkt-primary/50 animate-in fade-in slide-in-from-bottom-2">
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10 ${tone}`} />
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl text-white ${tone}`}><Icon className="h-5 w-5" /></div>
        <span className="text-xs font-semibold uppercase tracking-wide text-pawkt-text-secondary">{label}</span>
        <ArrowUpRight className="ml-auto h-4 w-4 text-pawkt-text-disabled transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-pawkt-primary" />
      </div>
      <div className="mt-3 font-display text-3xl font-extrabold tracking-tight text-pawkt-text" data-testid={`${testid}-value`}>{value}</div>
      <div className="mt-1 text-xs text-pawkt-text-disabled">{sub}</div>
    </button>
  );
}

export function CustomerIntelligence({ onTotalClick }) {
  const navigate = useNavigate();
  const [breedsOpen, setBreedsOpen] = useState(false);
  const { data, isError } = useQuery({
    queryKey: ["customer-stats"],
    queryFn: async () => (await api.get("/customers/stats")).data,
  });
  if (isError) return null;
  const d = data || {};
  const empty = d.total_customers === 0;
  return (
    <>
      <div className="mb-5 grid gap-3 sm:grid-cols-3" data-testid="customer-intelligence">
        <Card testid="ci-total-customers" icon={Users} label="Total customers" tone="bg-pawkt-primary" delay={0} onClick={onTotalClick}
          aria="Open the customer list" value={fmt(d.total_customers)} sub={empty ? "Add your first pet parent to get started" : "Unique active customers in this business"} />
        <Card testid="ci-breed-diversity" icon={Dna} label="Breed diversity" tone="bg-emerald-600" delay={60} onClick={() => setBreedsOpen(true)}
          aria="Open breed analytics" value={fmt(d.distinct_breeds)} sub={d.distinct_breeds ? "Distinct breeds across your customers' pets" : "No breeds recorded yet"} />
        <Card testid="ci-total-pets" icon={PawPrint} label="Pets in care" tone="bg-amber-500" delay={120} onClick={() => navigate("/pets?in_care=1")}
          aria="Open the list of pets in care" value={fmt(d.total_pets)}
          sub={d.customers_with_pets ? `Linked to ${fmt(d.customers_with_pets)} customer${d.customers_with_pets === 1 ? "" : "s"}` : "No pets linked yet"} />
      </div>
      {breedsOpen && <BreedAnalytics onClose={() => setBreedsOpen(false)} />}
    </>
  );
}
